import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";
import { ProcessTimeline } from "@/components/common/ProcessTimeline";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Select } from "@/components/design/Select";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ListingProcessSettings } from "./ListingProcessSettings";
import { ListingPowerQuizRequest } from "./ListingPowerQuizRequest";
import { ListingApplicationMails } from "./ListingApplicationMails";
import { ListingMailNotificationSwitches } from "./ListingMailNotificationSwitches";
import { usePendingMailRefresh } from "./use-pending-mail-refresh";
import { readApplicationProcess, type ApplicationProcess } from "./listing-application-process";
import { DocumentView } from "@/components/documents/DocumentView";
import { createDocumentResource, downloadDocument } from "@/lib/document-resource";
import { openDocumentResource } from "@/stores/document-preview-store";
import { FormattedMessage, useIntl } from "react-intl";
import { LoadMore } from "@/components/common/LoadMore";
import { PersonLink } from "@/components/common/PersonLink";
import { ListingApplicantAccess } from "./ListingApplicantAccess";
import { Button } from "@/components/design/Button";
import { FilterChip } from "@/components/design/FilterChip";
import {
  ListingApplicationProcessDocument,
  SetListingApplicationStageDocument,
  DocumentClassifier,
  ListingApplicationState,
  ListingApplicationsDocument,
  OrganizationalRecordDocumentContentDocument,
  SetListingApplicationStateDocument,
  SortOrder,
  type ListingApplicationRowFragment,
} from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { usePagedQuery } from "@/lib/use-paged-query";
import { base64ToBlob } from "@/lib/file-bytes";
import { userErrorMessage } from "@/lib/user-error";
import {
  APPLICATION_REVIEW_LABELS,
  availableReviews,
  LISTING_APPLICATION_STATE_LABELS,
  LISTING_APPLICATION_STATES,
  type ApplicationReviewSpec,
} from "@/pages/share/applications/listing-application-state";

// Die Bewerbungen einer Ausschreibung, die Fläche "Bewerbungen" ihres Dashboards, für die, die sie lesen dürfen:
// der Ersteller und wer auf Bewerbungen Lesen oder Bearbeiten hat, nicht schon jedes Mitglied
// (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers). Ob das zutrifft, sagt
// `actions.canReadApplications`; welche Zustandswechsel eine Zeile anbietet, steht allein in
// `ListingApplication.actions`.
//
// Die Dokumente liegen in der Organisationsakte der bewerbenden Person. genesis liefert sie nur mit
// einem Akten-Recht und sonst null; dann fehlt der Abschnitt ganz, statt "nichts eingereicht" zu
// behaupten. Das Profil der Person öffnet der Personen-Link, mit den Rechten, die dort gelten.

type Filter = "all" | ListingApplicationState;
type Application = ListingApplicationRowFragment;
type ApplicationDocument = NonNullable<Application["documents"]>[number];

/** Für Mitglieder ohne Leserecht: kein Zugriff, ausdrücklich, statt einer leeren Liste. */
export function ListingApplicationsNoAccess() {
  return (
    <div id="listing-applications-no-access" className="max-w-[62ch] space-y-2 py-4">
      <h3 className="text-smycles-title font-medium">
        <FormattedMessage id="share.listings.applications.noAccess" defaultMessage="Kein Zugriff auf Bewerbungen" />
      </h3>
      <p className="text-smycles-body text-ink">
        <FormattedMessage
          id="share.listings.applications.noAccessHint"
          defaultMessage="Bewerbungen sieht nur, wer sie ausdrücklich lesen darf. Zugriff vergibt, wer die Zugriffe dieser Ausschreibung verwaltet."
        />
      </p>
    </div>
  );
}

export function ListingApplicationsFacet({ listingId }: { listingId: string }) {
  const intl = useIntl();
  const [view, setView] = useState<"list" | "process">("list");
  const [settings, setSettings] = useState(false);
  const processQuery = useQuery(ListingApplicationProcessDocument, { variables: { id: listingId } });
  const process = readApplicationProcess(processQuery.data?.listing?.applicationProcess);
  const [filter, setFilter] = useState<Filter>("all");
  const input = useMemo(
    () => ({
      order: { receivedOn: SortOrder.Desc },
      // genesis' FilterSpec wertet nur das ERSTE gesetzte Feld aus und verwirft weitere still;
      // Ausschreibung und Zustand gehen deshalb zusammen nur über `and`.
      where:
        filter === "all"
          ? { listingId: { eq: listingId } }
          : { and: [{ listingId: { eq: listingId } }, { state: { eq: filter } }] },
    }),
    [filter, listingId],
  );
  const { nodes, totalCount, loading, error, hasMore, loadingMore, loadMoreFailed, loadMore } =
    usePagedQuery<Application>(ListingApplicationsDocument, {
      connectionKey: "listingApplications",
      input,
      pageSize: 24,
    });
  usePendingMailRefresh(nodes);

  const processGroups = process ? [
    ...process.stages.map(stage => ({ ...stage, applications: nodes.filter(application =>
      (application.state === ListingApplicationState.Received || application.state === ListingApplicationState.InReview)
      && (application.stageId ?? (application.state === ListingApplicationState.Received ? "inbox" : "review")) === stage.id) })),
    { id: ListingApplicationState.Accepted, name: intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[ListingApplicationState.Accepted]),
      applications: nodes.filter(application => application.state === ListingApplicationState.Accepted) },
  ].filter(group => filter === "all" || group.applications.length > 0) : [];
  const exitedGroups = [ListingApplicationState.Rejected, ListingApplicationState.Withdrawn]
    .map(state => ({ state, applications: nodes.filter(application => application.state === state) }))
    .filter(group => filter === "all" || group.applications.length > 0);

  return (
    // Ohne eigenen Kasten und ohne eigenen Titel: die Fläche steht im Blatt des Dashboards, und ihr
    // Reiter heißt schon "Bewerbungen". Die Zahl steht deshalb neben den Filtern.
    <div id="listing-applications">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <SegmentedControl id="listing-applications-view" value={view} onChange={setView} options={[
          { value: "list", label: intl.formatMessage({ id: "share.listings.process.list" }) },
          { value: "process", label: intl.formatMessage({ id: "share.listings.process.view" }), disabled: !process },
        ]} />
        {processQuery.data?.listing?.actions.canUpdate && process ? <Button id="listing-process-settings-open" variant="secondary" size="compact"
          onClick={() => setSettings(true)}>{intl.formatMessage({ id: "share.listings.process.settings" })}</Button> : null}
      </div>
      <ListingMailNotificationSwitches listingId={listingId} />
      {processQuery.error ? <p role="alert" className="mb-4 text-smycles-meta text-ink-accent">{userErrorMessage(intl, processQuery.error)}</p> : null}
      {settings && process ? <ListingProcessSettings listingId={listingId} process={process} onClose={() => setSettings(false)} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip id="listing-applications-filter-all" active={filter === "all"} onClick={() => setFilter("all")}>
          <FormattedMessage id="share.listings.filterAll" defaultMessage="Alle" />
        </FilterChip>
        {LISTING_APPLICATION_STATES.map((state) => (
          <FilterChip id={`listing-applications-filter-${state}`} key={state} active={filter === state} onClick={() => setFilter(state)}>
            {intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[state])}
          </FilterChip>
        ))}
        {totalCount != null ? (
          <span id="listing-applications-count" className="smycles-field-meta ml-auto text-ink-muted">
            <FormattedMessage
              id="share.listings.applications.count"
              defaultMessage="{count, plural, one {# Bewerbung} other {# Bewerbungen}}"
              values={{ count: totalCount }}
            />
          </span>
        ) : null}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-smycles-body text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Laden…" />
          </div>
        ) : error ? (
          <p role="alert" className="text-smycles-body text-ink-muted">
            {userErrorMessage(intl, error)}
          </p>
        ) : filter !== "all" && nodes.length === 0 ? (
          <p className="smycles-field-value text-ink-muted">
            <FormattedMessage id="share.listings.applications.emptyFiltered" defaultMessage="Keine Bewerbung in diesem Zustand." />
          </p>
        ) : view === "process" && process ? (
          <>
            {hasMore ? <p className="mb-5 text-smycles-meta text-ink-muted">{intl.formatMessage({ id: "share.listings.process.loaded" }, { count: nodes.length })}</p> : null}
            {processGroups.length > 0 ? <ProcessTimeline>
              {processGroups.map(group => <li key={group.id} className="relative pb-7 last:pb-0">
                <span aria-hidden="true" className="absolute -left-[29px] top-[13px] h-2.5 w-2.5 rounded-full bg-ink" />
                <h3 className="py-2 text-smycles-body font-medium">{group.name} <span className="ml-2 text-smycles-meta font-normal text-ink-muted">{group.applications.length}</span></h3>
                <ul className="divide-y divide-ink/10">{group.applications.map(application =>
                  <ApplicationRow key={application.id} application={application} process={process} refreshAfterChange={filter !== "all"} />)}</ul>
              </li>)}
            </ProcessTimeline> : null}
            {exitedGroups.length > 0 ? <section className={processGroups.length ? "mt-8 border-t border-ink/10 pt-6" : ""} aria-label={intl.formatMessage({ id: "share.listings.process.exited" })}>
              <h3 className="mb-4 text-smycles-body font-medium">{intl.formatMessage({ id: "share.listings.process.exited" })}</h3>
              <div className="space-y-6">
                {exitedGroups.map(({ state, applications }) => {
                  return <section key={state}>
                    <h4 className="text-smycles-body text-ink">{intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[state])}{" "}
                      <span className="ml-2 text-smycles-meta text-ink-muted">{applications.length}</span>
                    </h4>
                    <ul className="divide-y divide-ink/10">{applications.map(application =>
                      <ApplicationRow key={application.id} application={application} process={process} refreshAfterChange={filter !== "all"} />)}</ul>
                  </section>;
                })}
              </div>
            </section> : null}
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        ) : nodes.length === 0 ? (
          <p className="smycles-field-value text-ink-muted">
            {filter === "all" ? (
              <FormattedMessage id="share.listings.applications.empty" defaultMessage="Noch keine Bewerbung." />
            ) : (
              <FormattedMessage
                id="share.listings.applications.emptyFiltered"
                defaultMessage="Keine Bewerbung in diesem Zustand."
              />
            )}
          </p>
        ) : (
          <>
            <div aria-hidden="true" className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)] gap-8 border-b border-ink/10 pb-4 text-smycles-meta text-ink-muted @4xl:grid">
              <span><FormattedMessage id="share.listings.applications.personColumn" defaultMessage="Bewerbende" /></span>
              <span><FormattedMessage id="share.listings.applications.documentsColumn" defaultMessage="Unterlagen" /></span>
              <span><FormattedMessage id="share.listings.applications.reviewColumn" defaultMessage="Bearbeitung" /></span>
            </div>
            <ul className="divide-y divide-ink/10">
              {nodes.map((application) => (
                <ApplicationRow key={application.id} application={application} process={process} refreshAfterChange={filter !== "all"} />
              ))}
            </ul>
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        )}
      </div>
    </div>
  );
}

function ApplicationRow({ application, process, refreshAfterChange }: { application: Application; process: ApplicationProcess | null; refreshAfterChange: boolean }) {
  const intl = useIntl();
  const [setState] = useMutation(SetListingApplicationStateDocument, { refetchQueries: refreshAfterChange ? [ListingApplicationsDocument] : [] });
  const [setStage] = useMutation(SetListingApplicationStageDocument, { refetchQueries: refreshAfterChange ? [ListingApplicationsDocument] : [] });
  const [decision, setDecision] = useState<ApplicationReviewSpec | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reviews = availableReviews(application.actions);
  const received = formatDateTime(application.receivedOn, intl.locale);
  const decided = formatDateTime(application.decidedOn, intl.locale);
  const person = application.person;

  async function review(spec: ApplicationReviewSpec) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      // Die Antwort trägt Zustand und neue `actions`; der normalisierte Cache setzt die Zeile neu.
      await setState({ variables: { input: { id: application.id, state: spec.target, dryRun: false } } });
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setPending(false);
    }
  }

  async function moveStage(stageId: string) {
    if (pending) return;
    setPending(true); setError(null);
    try { await setStage({ variables: { input: { id: application.id, stageId } } }); }
    catch (e) { setError(userErrorMessage(intl, e)); }
    finally { setPending(false); }
  }


  return (
    <li id={`listing-application-${application.id}`} className="py-6">
      <ConfirmDialog id={`listing-application-decision-${application.id}`} open={decision !== null} title={decision ? intl.formatMessage(APPLICATION_REVIEW_LABELS[decision.review]) : ""}
        destructive={decision?.target === ListingApplicationState.Rejected}
        onCancel={() => setDecision(null)} onConfirm={async () => { if (decision) { const value = decision; setDecision(null); await review(value); } }} />
      <div className="grid grid-cols-1 items-start gap-4 @4xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)] @4xl:gap-8">
        <div className="min-w-0">
          {application.companyName ? <p className="mb-2 text-smycles-body font-medium">{application.companyName}</p> : null}
          <PersonLink
            size="md"
            className="min-w-0 rounded text-smycles-body font-medium focus-visible:ring-2 focus-visible:ring-ink/30"
            person={{
              id: person?.id,
              name:
                person?.formattedName ||
                intl.formatMessage({
                  id: "share.listings.applications.unnamedPerson",
                  defaultMessage: "Person ohne Namen",
                }),
              avatarContent: person?.avatar?.content,
              avatarMime: person?.avatar?.mimeType,
            }}
            secondary={received ? (
              <FormattedMessage
                id="share.listings.applications.receivedOn"
                defaultMessage="Eingang {date}"
                values={{ date: received }}
              />
            ) : null}
          />
          {person?.id ? <ListingApplicantAccess key={person.id} personId={person.id} application={application} quizEnabled={process?.powerQuizEnabled ?? false} /> : null}
        </div>

        <div className="min-w-0">
          {application.documents ? (
            <div id={`listing-application-documents-${application.id}`}>
              {application.documents.length ? (
                <ul className="flex flex-col gap-2">
                  {application.documents.map((entry) => (
                    <li key={entry.document.id} className="max-w-full">
                      <ApplicationDocumentView entry={entry} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="smycles-field-meta text-ink-muted">
                  <FormattedMessage id="share.listings.applications.noDocuments" defaultMessage="Keine Dokumente eingereicht." />
                </p>
              )}
            </div>
          ) : null}
        </div>

        <div className="min-w-0 space-y-4">
          <span id={`listing-application-state-${application.id}`} className="block text-smycles-body font-medium">
            {intl.formatMessage(LISTING_APPLICATION_STATE_LABELS[application.state])}
          </span>
          {decided ? (
            <p className="smycles-field-meta text-ink-muted">
              {application.state === ListingApplicationState.Withdrawn ? (
                <FormattedMessage id="share.listings.applications.withdrawnOn" defaultMessage="Zurückgezogen {date}" values={{ date: decided }} />
              ) : (
                <FormattedMessage id="share.listings.applications.decidedOn" defaultMessage="Entschieden {date}" values={{ date: decided }} />
              )}
            </p>
          ) : null}
          {process && application.actions.canMoveStage ? <label className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 text-smycles-meta text-ink-muted">
            {intl.formatMessage({ id: "share.listings.process.phase" })}
            <Select id={`listing-application-stage-${application.id}`} variant="plain" value={application.stageId} disabled={pending} hideEmpty
              options={process.stages.map(stage => ({ value: stage.id, label: stage.name }))}
              onChange={stageId => void moveStage(stageId)} />
          </label> : null}
          {reviews.length ? (
            <div className="flex flex-wrap gap-2">
              {reviews.map((spec) => (
                <Button
                  key={spec.review}
                  id={`listing-application-${spec.review}-${application.id}`}
                  variant="secondary"
                  size="compact"
                  disabled={pending}
                  onClick={() => spec.target === ListingApplicationState.Accepted || spec.target === ListingApplicationState.Rejected ? setDecision(spec) : void review(spec)}
                >
                  {intl.formatMessage(APPLICATION_REVIEW_LABELS[spec.review])}
                </Button>
              ))}
            </div>
          ) : null}
          {(process?.powerQuizEnabled && application.actions.canRequestPowerQuiz) || application.powerQuizRequest ? <div className="border-t border-ink/10 pt-3">
            <ListingPowerQuizRequest application={application} />
          </div> : null}
          {application.actions.mailKinds.length || application.mails?.length ? <div className="border-t border-ink/10 pt-3">
            <ListingApplicationMails application={application} />
          </div> : null}
          {error ? <p role="alert" className="text-smycles-meta text-ink">{error}</p> : null}
        </div>
      </div>
    </li>
  );
}

// Uses the same public display boundary as SAVE and the organization record.
// The original stays behind the organization-record query and its access checks.
function ApplicationDocumentView({ entry }: { entry: ApplicationDocument }) {
  const client = useApolloClient();
  const intl = useIntl();
  const [error, setError] = useState<string | null>(null);
  const resource = useMemo(() => createDocumentResource({
    name: entry.document.fileName,
    load: async () => {
      const { data } = await client.query({
        query: OrganizationalRecordDocumentContentDocument,
        variables: { id: entry.document.id },
        fetchPolicy: "no-cache",
      });
      const file = data?.organizationalRecordDocument?.content;
      if (!file?.content) throw new Error("Missing organizational record document content");
      return base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
    },
  }), [client, entry.document.id, entry.document.fileName]);
  const act = async (action: typeof openDocumentResource) => {
    setError(null);
    try { await action(resource); }
    catch (cause) { setError(userErrorMessage(intl, cause)); }
  };
  return <>
    <DocumentView variant="library-row" file={resource}
      idPrefix={`listing-application-document-${entry.document.id}`}
      kind={entry.document.fileName.split(".").pop()?.slice(0, 4).toUpperCase() ?? ""}
      name={entry.document.title} when={null} source=""
      sourceDisplay={entry.classifier === DocumentClassifier.CurriculumVitae
        ? intl.formatMessage({ id: "share.applications.documents.cv", defaultMessage: "Lebenslauf" }) : undefined}
      isRenaming={false} selected={false} onRename={() => {}} onStopRename={() => {}}
      onOpen={() => void act(openDocumentResource)} onDownload={() => void act(downloadDocument)}
    />
    {error ? <p role="alert" className="mt-2 text-smycles-meta text-ink">{error}</p> : null}
  </>;
}
